import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../services/api';
import { BookingCreate, TableOut, EventOut } from '../../../types/api';
import Card from '../../atoms/Card';
import Button from '../../atoms/Button';
import './BookingForm.scss';

interface BookingFormProps {
  isOpen: boolean;
  onClose: () => void;
  table: TableOut;
  event: EventOut;
  onBookingSuccess: () => void;
}

const BookingForm: React.FC<BookingFormProps> = ({
  isOpen,
  onClose,
  table,
  event,
  onBookingSuccess
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    booking_date: '',
    start_time: '',
    end_time: '',
    number_of_people: table.capacity,
    special_requests: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    contact_instagram: '',
    is_on_behalf: false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const bookingData: BookingCreate = {
        table_id: table.id,
        event_id: event.id,
        booking_date: formData.booking_date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        number_of_people: formData.number_of_people,
        special_requests: formData.special_requests || null,
        contact_name: formData.contact_name || null,
        contact_phone: formData.contact_phone || null,
        contact_email: formData.contact_email || null,
        contact_instagram: formData.contact_instagram || null,
        is_on_behalf: formData.is_on_behalf
      };

      await apiService.createBooking(bookingData);
      onBookingSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || t('events.booking.error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="booking-form-overlay">
      <div className="booking-form-modal">
        <Card variant="elevated" className="booking-form-card">
          <div className="booking-form-header">
            <h2 className="booking-form-title">
              {t('events.booking.title', { tableName: table.name, eventName: event.name })}
            </h2>
            <Button 
              variant="outline" 
              size="sm"
              onClick={onClose}
              className="booking-form-close"
            >
              ✕
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="booking-form">
            <div className="booking-form-section">
              <h3 className="booking-form-section-title">
                {t('events.booking.dateTime')}
              </h3>
              
              <div className="booking-form-row">
                <div className="booking-form-field">
                  <label htmlFor="booking_date">
                    {t('events.booking.date')} *
                  </label>
                  <input
                    type="date"
                    id="booking_date"
                    name="booking_date"
                    value={formData.booking_date}
                    onChange={handleInputChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div className="booking-form-row">
                <div className="booking-form-field">
                  <label htmlFor="start_time">
                    {t('events.booking.startTime')} *
                  </label>
                  <input
                    type="time"
                    id="start_time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="booking-form-field">
                  <label htmlFor="end_time">
                    {t('events.booking.endTime')} *
                  </label>
                  <input
                    type="time"
                    id="end_time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="booking-form-section">
              <h3 className="booking-form-section-title">
                {t('events.booking.details')}
              </h3>
              
              <div className="booking-form-row">
                <div className="booking-form-field">
                  <label htmlFor="number_of_people">
                    {t('events.booking.numberOfPeople')} *
                  </label>
                  <input
                    type="number"
                    id="number_of_people"
                    name="number_of_people"
                    value={formData.number_of_people}
                    onChange={handleInputChange}
                    min="1"
                    max={table.capacity}
                    required
                  />
                  <small>
                    {t('events.booking.maxCapacity', { capacity: table.capacity })}
                  </small>
                </div>
              </div>

              <div className="booking-form-row">
                <div className="booking-form-field">
                  <label htmlFor="special_requests">
                    {t('events.booking.specialRequests')}
                  </label>
                  <textarea
                    id="special_requests"
                    name="special_requests"
                    value={formData.special_requests}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder={t('events.booking.specialRequestsPlaceholder')}
                  />
                </div>
              </div>
            </div>

            <div className="booking-form-section">
              <h3 className="booking-form-section-title">
                {t('events.booking.contact')}
              </h3>
              
              <div className="booking-form-row">
                <div className="booking-form-field">
                  <label htmlFor="contact_name">
                    {t('events.booking.contactName')}
                  </label>
                  <input
                    type="text"
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleInputChange}
                    placeholder={t('events.booking.contactNamePlaceholder')}
                  />
                </div>
                <div className="booking-form-field">
                  <label htmlFor="contact_phone">
                    {t('events.booking.contactPhone')}
                  </label>
                  <input
                    type="tel"
                    id="contact_phone"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleInputChange}
                    placeholder={t('events.booking.contactPhonePlaceholder')}
                  />
                </div>
              </div>

              <div className="booking-form-row">
                <div className="booking-form-field">
                  <label htmlFor="contact_email">
                    {t('events.booking.contactEmail')}
                  </label>
                  <input
                    type="email"
                    id="contact_email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleInputChange}
                    placeholder={t('events.booking.contactEmailPlaceholder')}
                  />
                </div>
                <div className="booking-form-field">
                  <label htmlFor="contact_instagram">
                    {t('events.booking.contactInstagram')}
                  </label>
                  <input
                    type="text"
                    id="contact_instagram"
                    name="contact_instagram"
                    value={formData.contact_instagram}
                    onChange={handleInputChange}
                    placeholder={t('events.booking.contactInstagramPlaceholder')}
                  />
                </div>
              </div>

              <div className="booking-form-row">
                <div className="booking-form-field">
                  <label className="booking-form-checkbox">
                    <input
                      type="checkbox"
                      name="is_on_behalf"
                      checked={formData.is_on_behalf}
                      onChange={handleInputChange}
                    />
                    <span>{t('events.booking.isOnBehalf')}</span>
                  </label>
                </div>
              </div>
            </div>

            {error && (
              <div className="booking-form-error">
                <p>{error}</p>
              </div>
            )}

            <div className="booking-form-actions">
              <Button 
                type="button"
                variant="outline" 
                onClick={onClose}
                disabled={loading}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                type="submit"
                variant="primary" 
                disabled={loading}
              >
                {loading ? t('events.booking.creating') : t('events.booking.confirm')}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default BookingForm; 